/**
 * Command Palette Component
 *
 * A modern search interface inspired by Linear, Raycast, and VS Code.
 * Triggered by Cmd+K (Mac) or Ctrl+K (Windows/Linux).
 *
 * Features:
 * - Search users, instructions, cards, wallets, and support tickets
 * - Smart routing based on input patterns (instruction refs, card / wallet account numbers)
 * - Grouped results with rich info display
 * - Keyboard navigation
 * - Multi-layer caching for instant responses
 * - Request cancellation and deduplication
 * - Ask AI command to quickly access AI colleagues
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTabNavigationSafe } from '@/context/TabsContext';
import { useCommandPaletteContext } from '@/context/CommandPaletteContext';
import { useAIColleaguesContext } from '@/context/AIColleaguesContext';
import { Modal, Input, Empty, Spin, Typography, Segmented } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  IdcardOutlined,
  CustomerServiceOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  SwapOutlined,
  AppstoreOutlined,
  WalletOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SyncOutlined,
  RobotOutlined,
  MessageOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { useAuth } from '@/context/AuthContext';
import {
  useCommandPaletteSearch,
  type CommandPaletteResult,
  type CommandPaletteResultType,
} from '@/hooks/useCommandPaletteSearch';
import { searchCache } from '@/services/search-cache';
import { MessageComposer } from './MessageComposer';
import './CommandPalette.less';

const { Text } = Typography;

type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
};

type PaletteMode = 'search' | 'message';

// Special command types that extend regular results
type CommandType = CommandPaletteResultType | 'action';

type CommandItem = Omit<CommandPaletteResult, 'type'> & {
  type: CommandType;
  action?: () => void;
};

// Domain ID to icon mapping
const DOMAIN_ICONS: Record<string, React.ReactNode> = {
  work: <ClockCircleOutlined />,
  customers: <UserOutlined />,
  instructions: <SwapOutlined />,
  cards: <CreditCardOutlined />,
  products: <AppstoreOutlined />,
  compliance: <SafetyCertificateOutlined />,
  backoffice: <SettingOutlined />,
};

/**
 * Get icon for a domain
 */
const getDomainIcon = (domainId: string): React.ReactNode => {
  return DOMAIN_ICONS[domainId] || <FileTextOutlined />;
};

// AI-related keywords that trigger the "Ask AI" command
const AI_KEYWORDS = ['ai', 'ask', 'help', 'assistant', 'colleague', 'question', 'how', 'what', 'why', 'explain'];

// Patterns that indicate a question (start of query)
const QUESTION_PATTERNS = [
  /^how\s/i,
  /^what\s/i,
  /^why\s/i,
  /^when\s/i,
  /^where\s/i,
  /^who\s/i,
  /^can\s(i|you|we)\s/i,
  /^should\s(i|we)\s/i,
  /^is\s(it|this|there)\s/i,
  /^do\s(i|we)\s/i,
  /^does\s/i,
  /^explain\s/i,
  /^help\s(me\s)?/i,
  /\?$/,  // Ends with question mark
];

/**
 * Detect if the query looks like a question that should go to AI
 */
const isQuestion = (q: string): boolean => {
  const trimmed = q.trim();
  if (trimmed.length < 5) return false;
  return QUESTION_PATTERNS.some(pattern => pattern.test(trimmed));
};

export const CommandPalette: React.FC<CommandPaletteProps> = ({ open, onClose }) => {
  const { openTab, duplicateTab, activeTabKey } = useTabNavigationSafe();
  const { forceNewTab } = useCommandPaletteContext();
  const { openAIColleagues } = useAIColleaguesContext();
  const { initialState } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mode, setMode] = useState<PaletteMode>('search');

  const supportCenterVendors = initialState?.supportCenterEnabledVendors || [];
  const hasSupportCenter = supportCenterVendors.length > 0;

  // Detect OS for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  // Use the optimized search hook with caching
  const { results, loading, isStale, menuItems, flatResults: searchResults } = useCommandPaletteSearch(
    query,
    open,
    {
      userAcl: initialState?.currentUser?.acl,
      hasSupportCenter,
      supportCenterVendors,
      ticketCacheScopeKey: [
        initialState?.currentUser?.id ?? 'anonymous',
        supportCenterVendors.map(({ id }) => id).sort((a, b) => a - b).join(','),
        JSON.stringify(initialState?.currentUser?.acl ?? null),
      ].join(':'),
      debounceMs: 150,
    }
  );

  // Check if query looks like a question
  const queryIsQuestion = useMemo(() => isQuestion(query), [query]);

  // Check if query matches AI keywords
  const matchesAIKeyword = useMemo(() => {
    if (!query.trim()) return false;
    const lowerQuery = query.toLowerCase();
    return AI_KEYWORDS.some(keyword => lowerQuery.includes(keyword));
  }, [query]);

  // Create the "Ask AI" command (generic)
  const askAICommand: CommandItem = useMemo(() => ({
    id: 'ask-ai',
    type: 'action',
    title: 'Ask Digital Staff',
    subtitle: `Open Digital Staff (${shortcutKey}+J)`,
    description: 'Get help from expert digital colleagues',
    path: '',
    icon: <RobotOutlined style={{ color: '#722ed1' }} />,
    action: () => {
      onClose();
      setTimeout(() => openAIColleagues(), 100);
    }
  }), [shortcutKey, onClose, openAIColleagues]);

  // Create the "Duplicate Current Tab" command
  const duplicateTabCommand: CommandItem | null = useMemo(() => {
    if (!activeTabKey) return null;
    return {
      id: 'duplicate-tab',
      type: 'action',
      title: 'Duplicate Current Tab',
      subtitle: 'Clone the current tab',
      description: 'Create a copy of the current tab',
      path: '',
      icon: <CopyOutlined style={{ color: '#1890ff' }} />,
      action: () => {
        duplicateTab(activeTabKey);
        onClose();
      }
    };
  }, [activeTabKey, duplicateTab, onClose]);

  // Create the "Ask Riley" command with the question pre-filled
  const askRileyWithQuestion: CommandItem | null = useMemo(() => {
    if (!queryIsQuestion) return null;
    const trimmedQuery = query.trim();
    return {
      id: 'ask-riley-question',
      type: 'action',
      title: `Ask Riley: "${trimmedQuery.length > 40 ? trimmedQuery.slice(0, 40) + '...' : trimmedQuery}"`,
      subtitle: 'Get an answer from the Operations Lead',
      description: 'Riley can help with questions about processes, operations, and platform features',
      path: '',
      icon: <RobotOutlined style={{ color: '#722ed1' }} />,
      action: () => {
        onClose();
        setTimeout(() => {
          openAIColleagues({
            colleagueId: 'riley',
            initialMessage: trimmedQuery
          });
        }, 100);
      }
    };
  }, [queryIsQuestion, query, onClose, openAIColleagues]);

  // Combine results with AI command
  // Combine results - when no query, use quickNavItems for keyboard navigation
  const flatResults = useMemo((): CommandItem[] => {
    const hasQuery = query.trim().length > 0;
    
    // When no query, use quickNavItems (which includes askAICommand + menu items)
    if (!hasQuery) {
      const navItems: CommandItem[] = [askAICommand];
      if (duplicateTabCommand) navItems.push(duplicateTabCommand);
      navItems.push(...menuItems.map((item) => ({
        ...item,
        icon: getDomainIcon(item.subtitle?.split(' → ')[0]?.toLowerCase() || ''),
      })).slice(0, 13));
      return navItems;
    }
    
    const items: CommandItem[] = [];

    // If query looks like a question, show "Ask Riley" at the top
    if (askRileyWithQuestion) {
      items.push(askRileyWithQuestion);
    } else if (matchesAIKeyword) {
      // Otherwise show generic AI command if matches keywords
      items.push(askAICommand);
    }

    // Add duplicate tab command if query matches
    const lowerQuery = query.toLowerCase();
    if (duplicateTabCommand && (lowerQuery.includes('duplicate') || lowerQuery.includes('clone') || lowerQuery.includes('copy tab'))) {
      items.push(duplicateTabCommand);
    }

    // Add search results
    items.push(...searchResults);

    return items;
  }, [askRileyWithQuestion, matchesAIKeyword, askAICommand, searchResults, duplicateTabCommand, query, menuItems]);

  // Focus input and reset state when modal opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
      setQuery('');
      setSelectedIndex(0);
      setMode('search'); // Reset to search mode when opening

      // Prefetch recent searches in background
      const recentSearches = searchCache.getRecentSearches();
      if (recentSearches.length > 0) {
        // The cache will handle these - just trigger the searches
        // They'll populate the cache for instant results on repeat searches
      }
    }
  }, [open]);

  // Handle mode change
  const handleModeChange = useCallback((value: string | number) => {
    setMode(value as PaletteMode);
  }, []);

  // Scroll selected item into view
  useEffect(() => {
    const selectedEl = resultsRef.current?.querySelector('.command-palette-item.selected');
    selectedEl?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [flatResults.length]);

  const handleQueryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    setSelectedIndex(0);
  }, []);

  const handleSelect = useCallback(
    (result: CommandItem) => {
      if (result.type === 'action' && result.action) {
        result.action();
      } else {
        openTab(result.path, result.title, { forceNew: forceNewTab });
        onClose();
      }
    },
    [openTab, onClose, forceNewTab]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[selectedIndex]) {
            handleSelect(flatResults[selectedIndex]);
          }
          break;
        case 'Escape':
          onClose();
          break;
      }
    },
    [flatResults, selectedIndex, handleSelect, onClose]
  );

  const getIconForType = (type: CommandType) => {
    switch (type) {
      case 'user':
        return <UserOutlined />;
      case 'instruction':
        return <FileTextOutlined />;
      case 'invoice':
        return <FileTextOutlined />;
      case 'card':
        return <CreditCardOutlined />;
      case 'card_account':
        return <IdcardOutlined />;
      case 'wallet':
        return <WalletOutlined />;
      case 'ticket':
        return <CustomerServiceOutlined />;
      case 'action':
        return <RobotOutlined />;
      default:
        return <FileTextOutlined />;
    }
  };

  const renderResultItem = (
    result: CommandItem,
    _index: number,
    globalIndex: number
  ) => (
    <div
      key={`${result.type}-${result.id}`}
      className={`command-palette-item ${globalIndex === selectedIndex ? 'selected' : ''}`}
      onClick={() => handleSelect(result)}
      onMouseEnter={() => setSelectedIndex(globalIndex)}
    >
      <span className="command-palette-item-icon">
        {result.icon || getIconForType(result.type)}
      </span>
      <div className="command-palette-item-content">
        <div className="command-palette-item-main">
          <span className="command-palette-item-title">{result.title}</span>
          {result.subtitle && (
            <span className="command-palette-item-subtitle">{result.subtitle}</span>
          )}
        </div>
        {result.description && (
          <span className="command-palette-item-description">{result.description}</span>
        )}
      </div>
      <div className="command-palette-item-meta">{result.tags}</div>
    </div>
  );

  const renderSection = (
    title: string,
    items: CommandItem[],
    startIndex: number,
    color: string
  ) => {
    if (items.length === 0) return null;
    return (
      <div className="command-palette-section">
        <div className="command-palette-section-header">
          <span className="command-palette-section-title" style={{ color }}>
            {title}
          </span>
          <span className="command-palette-section-count">{items.length}</span>
        </div>
        {items.map((item, i) => renderResultItem(item, i, startIndex + i))}
      </div>
    );
  };

  const hasResults = flatResults.length > 0;

  // Calculate start indices for each section (accounting for AI command if present)
  const aiCommandOffset = askRileyWithQuestion || matchesAIKeyword ? 1 : 0;
  let idx = aiCommandOffset;
  const pageStartIdx = idx;
  idx += results.pages.length;
  const userStartIdx = idx;
  idx += results.users.length;
  const instructionStartIdx = idx;
  idx += results.instructions.length;
  const invoiceStartIdx = idx;
  idx += results.invoices.length;
  const cardStartIdx = idx;
  idx += results.cards.length;
  const cardAccountStartIdx = idx;
  idx += results.cardAccounts.length;
  const walletStartIdx = idx;
  idx += results.wallets.length;
  const ticketStartIdx = idx;

  // Add icons to menu items for display
  const menuItemsWithIcons = menuItems.map((item) => ({
    ...item,
    icon: getDomainIcon(item.subtitle?.split(' → ')[0]?.toLowerCase() || ''),
  }));

  // Add AI command to quick navigation (shown when no query)
  const quickNavItems: CommandItem[] = useMemo(() => {
    const navItems: CommandItem[] = [askAICommand];
    if (duplicateTabCommand) navItems.push(duplicateTabCommand);
    navItems.push(...menuItemsWithIcons.slice(0, 13));
    return navItems;
  }, [askAICommand, menuItemsWithIcons, duplicateTabCommand]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      maskClosable={true}
      centered
      width={640}
      className="command-palette-modal"
      destroyOnClose
    >
      <div className="command-palette" onKeyDown={mode === 'search' ? handleKeyDown : undefined}>
        {/* Mode Tabs */}
        <div className="command-palette-mode-tabs">
          <Segmented
            value={mode}
            onChange={handleModeChange}
            options={[
              { value: 'search', icon: <SearchOutlined />, label: 'Search' },
              { value: 'message', icon: <MessageOutlined />, label: 'Message' },
            ]}
            size="small"
          />
        </div>

        {mode === 'search' ? (
          <>
            {/* Search Input */}
            <div className="command-palette-input-wrapper">
              <SearchOutlined className="command-palette-search-icon" />
              <Input
                ref={inputRef}
                value={query}
                onChange={handleQueryChange}
                placeholder="Search or ask a question..."
                variant="borderless"
                className="command-palette-input"
              />
              <div className="command-palette-shortcuts">
                {isStale && (
                  <span className="command-palette-stale-indicator" title="Refreshing cached results">
                    <SyncOutlined spin style={{ fontSize: 12, marginRight: 8, color: '#8c8c8c' }} />
                  </span>
                )}
                <kbd>↑↓</kbd>
                <span>navigate</span>
                <kbd>↵</kbd>
                <span>open</span>
                <kbd>esc</kbd>
                <span>close</span>
          </div>
        </div>

        {/* Results */}
        <div className="command-palette-results" ref={resultsRef}>
          {loading && !isStale ? (
            <div className="command-palette-loading">
              <Spin size="small" />
              <span>Searching...</span>
        </div>
          ) : query ? (
            hasResults ? (
              <>
                  {/* Show AI command at top if query is a question or matches AI keywords */}
                  {(askRileyWithQuestion || matchesAIKeyword) && (
                    <div className="command-palette-section">
                      <div className="command-palette-section-header">
                        <span className="command-palette-section-title" style={{ color: '#722ed1' }}>
                          {askRileyWithQuestion ? 'Ask Riley' : 'Digital Staff'}
                        </span>
                      </div>
                      {renderResultItem(askRileyWithQuestion || askAICommand, 0, 0)}
                    </div>
                  )}
                {renderSection('Pages', results.pages, pageStartIdx, '#8c8c8c')}
                {renderSection('Users', results.users, userStartIdx, '#1890ff')}
                {renderSection(
                  'Instructions',
                  results.instructions,
                  instructionStartIdx,
                  '#52c41a'
                )}
                {renderSection('Invoices', results.invoices, invoiceStartIdx, '#13c2c2')}
                {renderSection('Cards', results.cards, cardStartIdx, '#722ed1')}
                {renderSection(
                  'Card accounts',
                  results.cardAccounts,
                  cardAccountStartIdx,
                  '#531dab'
                )}
                {renderSection('Wallets', results.wallets, walletStartIdx, '#2f54eb')}
                {renderSection('Tickets', results.tickets, ticketStartIdx, '#fa8c16')}
              </>
            ) : (
              <div className="command-palette-section">
                <div className="command-palette-zero-hint">
              <Text type="secondary">
                            Search by name (any order), email, account number (5+ digits), instruction or invoice ID,
                    card number, card account number, or wallet account number
              </Text>
                </div>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <div className="command-palette-empty-text">
                      <Text type="secondary">No results for "{query}"</Text>
                      <Text type="secondary" className="command-palette-empty-hint">
                        Try searching by name (any order), email, account number, wallet account, card or card account
                        number, or instruction / invoice reference ID
                      </Text>
                    </div>
                  }
                  className="command-palette-empty"
                />
          </div>
            )
          ) : (
            <div className="command-palette-section">
              <div className="command-palette-zero-hint">
                <Text type="secondary">
                          Search pages, users (name in any order), account numbers (5+ digits), instructions, invoices,
                  cards, card accounts, wallet accounts, or support tickets
                </Text>
          </div>
              <div className="command-palette-section-header">
                    <span className="command-palette-section-title">Quick Actions</span>
                <HistoryOutlined style={{ color: '#bfbfbf', fontSize: 12 }} />
          </div>
                  {quickNavItems.map((item, i) => renderResultItem(item, i, i))}
        </div>
          )}
        </div>

        {/* Footer */}
        <div className="command-palette-footer">
          <div className="command-palette-footer-hints">
            <span className="command-palette-hint">
              <Text type="secondary">
                Tip: Press <kbd>{shortcutKey}</kbd><kbd>J</kbd> to ask Digital Staff for help
              </Text>
            </span>
          </div>
          <div className="command-palette-footer-keys">
            <kbd>{shortcutKey}</kbd>
            <kbd>K</kbd>
          </div>
        </div>
        </>
        ) : (
          /* Message Mode */
          <MessageComposer onClose={onClose} />
        )}
      </div>
    </Modal>
  );
};

// Hook to handle global keyboard shortcut for Command Palette (Cmd+K)
export const useCommandPalette = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return {
    open,
    setOpen,
    onClose: () => setOpen(false),
  };
};

export default CommandPalette;
